import { useState, useEffect, useCallback } from 'react';
import { Plus, X, Trash2, Edit3, StickyNote, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

interface Note {
  id: string;
  title: string;
  content: string | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

type NoteColor = 'white' | 'yellow' | 'blue' | 'green' | 'pink';

const noteColors: Record<NoteColor, { bg: string; border: string; text: string }> = {
  white: { bg: 'bg-white', border: 'border-rule', text: 'text-ink' },
  yellow: { bg: 'bg-sun-soft', border: 'border-sun/30', text: 'text-sun-deep' },
  blue: { bg: 'bg-cobalt-soft', border: 'border-cobalt/20', text: 'text-cobalt' },
  green: { bg: 'bg-sage-soft', border: 'border-sage/30', text: 'text-sage-deep' },
  pink: { bg: 'bg-coral-soft', border: 'border-coral/20', text: 'text-coral' },
};

const colorOptions: { id: NoteColor; label: string; cls: string }[] = [
  { id: 'white', label: 'Weiß', cls: 'bg-white border-rule' },
  { id: 'yellow', label: 'Gelb', cls: 'bg-sun-soft border-sun/30' },
  { id: 'blue', label: 'Blau', cls: 'bg-cobalt-soft border-cobalt/20' },
  { id: 'green', label: 'Grün', cls: 'bg-sage-soft border-sage/30' },
  { id: 'pink', label: 'Rosa', cls: 'bg-coral-soft border-coral/20' },
];

function colorFromTags(tags: string[]): NoteColor {
  const colorTag = tags?.find((t) => t.startsWith('color:'));
  const c = colorTag?.replace('color:', '') as NoteColor;
  return noteColors[c] ? c : 'white';
}

function tagsWithColor(tags: string[], color: NoteColor): string[] {
  return [...(tags ?? []).filter((t) => !t.startsWith('color:')), `color:${color}`];
}

export default function NotesView() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [form, setForm] = useState({ title: '', content: '', color: 'white' as NoteColor });

  const loadNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.notes.list();
      setNotes(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const openCreate = () => {
    setEditingNote(null);
    setForm({ title: '', content: '', color: 'white' });
    setShowDialog(true);
  };

  const openEdit = (note: Note) => {
    setEditingNote(note);
    setForm({ title: note.title, content: note.content || '', color: colorFromTags(note.tags) });
    setShowDialog(true);
    setViewingNote(null);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingNote(null);
    setForm({ title: '', content: '', color: 'white' });
  };

  const handleSave = async () => {
    if (!form.title.trim() || isSaving) return;
    try {
      setIsSaving(true);
      setError(null);
      const tags = tagsWithColor(editingNote?.tags ?? [], form.color);
      if (editingNote) {
        await api.notes.update(editingNote.id, { title: form.title, content: form.content, tags });
      } else {
        await api.notes.create({ title: form.title, content: form.content, tags });
      }
      await loadNotes();
      closeDialog();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.notes.delete(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      if (viewingNote?.id === id) setViewingNote(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ==================== DIALOG ====================
  if (showDialog) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="sl-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="sl-h2">{editingNote ? 'Notiz bearbeiten' : 'Neue Notiz'}</h2>
            <button onClick={closeDialog} className="p-2 rounded-lg hover:bg-paper-deep transition-colors">
              <X className="w-5 h-5 text-ink-mute" />
            </button>
          </div>

          {error && <p className="text-coral text-sm mb-4">{error}</p>}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Titel *</label>
              <input
                type="text"
                placeholder="z.B. Ideen für den nächsten Elternabend"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="sl-input"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Farbe</label>
              <div className="flex gap-2">
                {colorOptions.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setForm({ ...form, color: c.id })}
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${c.cls} ${
                      form.color === c.id ? 'ring-2 ring-cobalt ring-offset-2' : ''
                    }`}
                    title={c.label}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Inhalt</label>
              <textarea
                placeholder="Deine Notizen..."
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="sl-input min-h-[200px] py-3 resize-none"
              />
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button onClick={closeDialog} className="sl-button sl-button-outline">
                Abbrechen
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || isSaving}
                className="sl-button sl-button-primary disabled:opacity-50 gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingNote ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== NOTE DETAIL ====================
  if (viewingNote) {
    const color = noteColors[colorFromTags(viewingNote.tags)];
    return (
      <div className="max-w-2xl mx-auto">
        <div className={`sl-card p-6 ${color.bg} ${color.border} border`}>
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setViewingNote(null)}
              className="sl-button sl-button-outline text-sm px-3 py-1.5 h-auto"
            >
              ← Zurück
            </button>
            <div className="flex gap-1">
              <button
                onClick={() => openEdit(viewingNote)}
                className="p-2 rounded-lg hover:bg-white/50 text-ink-mute hover:text-ink"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(viewingNote.id)}
                className="p-2 rounded-lg hover:bg-coral-soft text-ink-mute hover:text-coral"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <h2 className="sl-h2 mb-2">{viewingNote.title}</h2>
          <p className="text-sm text-ink-mute mb-4">
            {new Date(viewingNote.updatedAt).toLocaleDateString('de-DE')}
          </p>
          <div className="bg-white/60 rounded-xl p-4 min-h-[200px] whitespace-pre-wrap">
            {viewingNote.content || <span className="text-ink-faint italic">Kein Inhalt</span>}
          </div>
        </div>
      </div>
    );
  }

  // ==================== LOADING ====================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-cobalt animate-spin" />
      </div>
    );
  }

  // ==================== MAIN VIEW ====================
  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="sl-h2">Notizen</h2>
          <p className="text-sm text-ink-mute mt-1">{notes.length} Notizen</p>
        </div>
        <button onClick={openCreate} className="sl-button sl-button-primary gap-2">
          <Plus className="w-5 h-5" />
          Neue Notiz
        </button>
      </div>

      {error && (
        <div className="sl-card p-3 mb-4 border-coral bg-coral-soft text-coral text-sm">{error}</div>
      )}

      {notes.length === 0 ? (
        <div className="sl-card p-8 text-center">
          <StickyNote className="w-12 h-12 text-ink-faint mx-auto mb-4" />
          <h3 className="sl-h3 mb-2 text-ink-soft">Noch keine Notizen</h3>
          <p className="text-ink-mute mb-6">Sammle Ideen, Gedanken und Erinnerungen an einem Ort.</p>
          <button onClick={openCreate} className="sl-button sl-button-primary gap-2">
            <Plus className="w-5 h-5" />
            Erste Notiz erstellen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {notes.map((note) => {
            const colorKey = colorFromTags(note.tags);
            const color = noteColors[colorKey];
            return (
              <button
                key={note.id}
                onClick={() => setViewingNote(note)}
                className={`sl-card p-5 text-left ${color.bg} ${color.border} border hover:shadow-elevation-2 transition-all group relative`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className={`font-semibold ${color.text} line-clamp-2`}>{note.title}</h3>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(note); }}
                      className="p-1.5 rounded-lg hover:bg-white/50 text-ink-mute hover:text-ink"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                      className="p-1.5 rounded-lg hover:bg-coral-soft text-ink-mute hover:text-coral"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-ink-soft line-clamp-3 mb-3">
                  {note.content || <span className="text-ink-faint italic">Kein Inhalt</span>}
                </p>
                <p className="text-xs text-ink-faint">
                  {new Date(note.updatedAt).toLocaleDateString('de-DE')}
                </p>
              </button>
            );
          })}

          <button
            onClick={openCreate}
            className="sl-card p-5 border-dashed border-2 border-rule bg-transparent hover:bg-paper-deep transition-colors flex flex-col items-center justify-center gap-3 text-ink-mute min-h-[160px]"
          >
            <Plus className="w-8 h-8" />
            <span className="font-medium">Neue Notiz</span>
          </button>
        </div>
      )}
    </div>
  );
}
